// src/pages/dashboard/receptionist-settings/AICharacterSettings.tsx
import React, { useState, useEffect } from 'react';
import { Controller, Control, FieldErrors, UseFormSetValue, UseFormRegister, useWatch } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2, PlayCircle, Settings, CheckCircle, Volume2, User, Zap, Sparkles, ChevronDown, ChevronUp, Phone, PhoneIncoming, PhoneOutgoing, Clock, Lightbulb } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import InfoModal from '@/components/ui/InfoModal';
import { SettingsFormData } from './settingsTypes'; // Ensure AICharacterVoiceOption is part of settingsTypes or defined here
import { aiVoiceOptions } from './settingsConstants';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useVideoBySlug } from '@/hooks/useVideos';
import VideoLink from '@/components/ui/VideoLink';

// Define the base type for a single voice option from your constants
type BaseVoiceOption = typeof aiVoiceOptions[number];

// Define a more specific type for voice options used in this component
interface AICharacterVoiceOptionExtended extends BaseVoiceOption {
    personality: string;
    icon?: React.ElementType;
    popular?: boolean;
    traits?: string[];
}


interface AICharacterSettingsProps {
    control: Control<SettingsFormData>;
    errors: FieldErrors<SettingsFormData>;
    register: UseFormRegister<SettingsFormData>;
    setValue: UseFormSetValue<SettingsFormData>;
    handlePlaySample: (sampleUrl?: string | null, voiceId?: string) => void;
    playingAudioRef: React.MutableRefObject<HTMLAudioElement | null>;
    isSettingsDirty: boolean;
    isSaving: boolean;
    isSettingsFetching: boolean;
    isSetupComplete: boolean;
    currentSettings?: SettingsFormData; // Changed to optional
    // userAccountEmail?: string; // Removed as per request
}

// Helper for phone number formatting
const formatPhoneNumber = (value: string): string => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    if (phoneNumber.startsWith('1') && phoneNumber.length > 1) {
        const nationalNumber = phoneNumber.substring(1);
        const nationalNumberLength = nationalNumber.length;
        if (nationalNumberLength < 4) return `+1 (${nationalNumber}`;
        if (nationalNumberLength < 7) {
            return `+1 (${nationalNumber.slice(0, 3)}) ${nationalNumber.slice(3)}`;
        }
        return `+1 (${nationalNumber.slice(0, 3)}) ${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6, 10)}`;
    }
    const phoneNumberLength = phoneNumber.length;
     if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};


const voiceOptionsWithPersonality: AICharacterVoiceOptionExtended[] = aiVoiceOptions.map(voice => {
    let personality = "Clear & Standard";
    let icon = User;
    let popular = false;
    let traits = ["Reliable", "Clear", "Professional"];

    switch (voice.name.toLowerCase()) {
        case "jade":
            personality = "Friendly & Professional";
            popular = true;
            traits = ["Welcoming", "Efficient", "Clear"];
            icon = Sparkles;
            break;
        // case "miles":
        //     personality = "Warm & Trustworthy";
        //     traits = ["Reassuring", "Calm", "Dependable"];
        //     icon = ShieldCheck;
        //     break;
        case "toni":
            personality = "Confident & Energetic";
            traits = ["Direct", "Upbeat", "To-the-point"];
            icon = Zap;
            break;
        case "skye":
            personality = "Relaxed & Conversational";
            traits = ["Warm", "Trusted", "Approachable"];
            // icon = User; // Or another appropriate icon
            break;
        case "devon":
            personality = "Neutral & Helpful";
            traits = ["Steady", "Clear", "Supportive"];
            // icon = User;
            break;
        case "riley":
            personality = "Encouraging & Approachable";
            traits = ["Trust-building", "Positive", "Friendly"];
            // icon = User;
            break;
        case "quinn":
            personality = "Sharp & Efficient";
            traits = ["Fast-talking", "Go-getter", "Effective"];
            // icon = User; // Or something like Briefcase
            break;
        default:
            if (voice.description.toLowerCase().includes("friendly")) traits = ["Friendly", ...traits.slice(1)];
            if (voice.description.toLowerCase().includes("professional")) traits = ["Professional", ...traits.slice(1)];
            break;
    }
    return { ...voice, personality, icon, popular, traits: Array.from(new Set(traits)) };
});


const WaveformAnimation = ({isPlaying}: {isPlaying: boolean}) => {
    const variants = {
        initial: { scaleY: 0.2, opacity: 0.5 },
        playing: (i:number) => ({
            scaleY: Math.random() * 0.8 + 0.2,
            opacity: Math.random() * 0.5 + 0.5,
            transition: {
                duration: 0.2 + Math.random() * 0.2,
                repeat: Infinity,
                repeatType: "mirror" as const,
                delay: i * 0.05,
            }
        }),
        idle: { scaleY: 0.2, opacity: 0.5, transition: {duration: 0.3}}
    }

    return (
        <div className="flex items-center justify-center h-6 w-16 space-x-0.5">
            {[...Array(5)].map((_, i) => (
                <motion.div
                    key={i}
                    custom={i}
                    variants={variants}
                    initial="initial"
                    animate={isPlaying ? "playing" : "idle"}
                    className="w-1.5 h-full bg-blue-500 rounded-full"
                />
            ))}
        </div>
    )
}


export default function AICharacterSettings({
    control,
    errors,
    register,
    setValue,
    handlePlaySample,
    playingAudioRef,
    isSettingsDirty,
    isSaving,
    isSettingsFetching,
    isSetupComplete,
    currentSettings,
    // userAccountEmail // Removed
}: AICharacterSettingsProps) {
    const callBehaviorSettingsVideo = useVideoBySlug('call-behavior-settings');
    const replyToEmailVideo = useVideoBySlug('reply-to-email');
    const medicalPrivacyModeVideo = useVideoBySlug('medical-privacy-mode');
    const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(currentSettings?.ai_voice_id || null);
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
    const [activelyPlayingVoiceId, setActivelyPlayingVoiceId] = useState<string | null>(null);


    const watchedGreeting = useWatch({ control, name: 'ai_greeting_message' });
    const greetingCharCount = watchedGreeting?.length || 0;
    const maxGreetingChars = 300;

    useEffect(() => {
        // Removed auto-fill for reply-to email
        if (currentSettings?.ai_voice_id) {
            setSelectedVoiceId(currentSettings.ai_voice_id);
        }
    }, [currentSettings]); // Removed setValue from dependencies as it's stable

    const handleInternalPlaySample = (sampleUrl?: string | null, voiceId?: string) => {
        if (playingAudioRef.current && activelyPlayingVoiceId !== voiceId) {
            playingAudioRef.current.pause();
            playingAudioRef.current.currentTime = 0;
        }
        handlePlaySample(sampleUrl, voiceId);
        setActivelyPlayingVoiceId(voiceId || null);

        if (playingAudioRef.current) {
            playingAudioRef.current.onended = () => setActivelyPlayingVoiceId(null);
            playingAudioRef.current.onpause = () => {
                if (playingAudioRef.current && playingAudioRef.current.currentTime !== playingAudioRef.current.duration && activelyPlayingVoiceId === voiceId) {
                   // Paused but not by new sample
                } else if (activelyPlayingVoiceId !== voiceId) {
                    // Paused because a different sample started
                }
                 else {
                    setActivelyPlayingVoiceId(null);
                }
            };
        }
    };

    const selectedVoiceDetails = voiceOptionsWithPersonality.find(v => v.id === selectedVoiceId);
    const businessNameToUse = currentSettings?.business_name || '[Your Business Name]';
    const aiNameToUse = selectedVoiceDetails?.name || 'your AI assistant';


    return (
        <Card className="shadow-lg">
            <CardHeader>
                <CardTitle className="flex items-center text-2xl">
                    <User className="mr-3 h-7 w-7 text-indigo-600" />
                    Your AI Employee
                </CardTitle>
                <CardDescription>Give your AI a voice and personality that represents your business.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-12">
                {/* Voice Selection Hero */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="p-6 bg-white rounded-xl shadow-xl border border-indigo-100"
                >
                    <div className="flex items-center mb-2">
                        <Label className="text-xl font-semibold block text-indigo-700">Choose Your AI's Voice</Label>
                        <InfoModal 
                            title="AI Voice Selection" 
                            description={
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-700 mb-4">
                                        Choose the voice personality that best represents your brand. This voice will be used for all calls handled by your AI Employee.
                                    </p>
                                    
                                    <div className="space-y-3">
                                        <div>
                                            <h4 className="font-medium mb-1">Voice Options:</h4>
                                            <ul className="text-sm space-y-1 text-gray-600">
                                                <li><strong>Jade:</strong> Most popular - friendly and professional</li>
                                                {/* <li><strong>Miles:</strong> Warm and trustworthy</li> */}
                                                <li><strong>Toni:</strong> Confident and energetic</li>
                                                <li><strong>Others:</strong> Each with unique personality traits</li>
                                            </ul>
                                        </div>
                                        
                                        <div>
                                            <h4 className="font-medium mb-1">How to Choose:</h4>
                                            <ul className="text-sm space-y-1 text-gray-600">
                                                <li>Click "Play Sample" to hear each voice</li>
                                                <li>Consider your industry and brand personality</li>
                                                <li>Think about your target customer preferences</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            }
                            triggerClassName="ml-2"
                        />
                    </div>
                    <p className="text-sm text-gray-600 mb-6">Pick the personality that matches your brand. This is how your AI will sound to customers.</p>

                    {!isSetupComplete && !selectedVoiceId && (
                        <p className="mb-4 text-center text-indigo-600 font-medium">
                            Let's bring your AI Employee to life! Pick a voice that represents your brand →
                        </p>
                    )}

                    <Controller
                        name="ai_voice_id"
                        control={control}
                        render={({ field }) => (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {voiceOptionsWithPersonality.map((voice) => {
                                    const isSelected = field.value === voice.id;
                                    const VoiceIcon = voice.icon || User;
                                    return (
                                        <motion.div
                                            key={voice.id}
                                            whileHover={{ y: -5, boxShadow: "0px 10px 20px rgba(0,0,0,0.1)"}}
                                            transition={{ type: "spring", stiffness: 300 }}
                                            onClick={() => {
                                                setValue('ai_voice_id', voice.id, { shouldDirty: true, shouldValidate: true });
                                                setValue('ai_voice_provider', voice.provider, { shouldDirty: true });
                                                setValue('ai_name', voice.name, { shouldDirty: true });
                                                setSelectedVoiceId(voice.id);
                                            }}
                                            className={cn(
                                                "cursor-pointer rounded-lg border bg-card text-card-foreground shadow-md transition-all duration-200 ease-in-out flex flex-col overflow-hidden",
                                                isSelected ? 'border-indigo-500 ring-2 ring-indigo-500 shadow-indigo-200/50' : 'border-gray-200 hover:border-indigo-300'
                                            )}
                                        >
                                            <CardHeader className="p-4 flex-row items-center space-x-3 bg-slate-50">
                                                <VoiceIcon className={cn("h-8 w-8", isSelected ? "text-indigo-600" : "text-gray-500")} />
                                                <div className="flex-grow">
                                                    <CardTitle className="text-lg font-semibold text-gray-800">{voice.name}</CardTitle>
                                                    <p className="text-xs text-indigo-500 font-medium">{voice.personality}</p>
                                                </div>
                                                {voice.popular && <div className="px-3 text-xs bg-yellow-200 text-black py-1 rounded-full flex items-center justify-center self-start">Most Popular</div>}
                                            </CardHeader>
                                            <CardContent className="p-4 flex-grow text-sm text-gray-600">
                                                {voice.description}
                                            </CardContent>
                                            <CardFooter className="p-3 flex items-center justify-between border-t border-gray-100 mt-auto bg-slate-50">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => { e.stopPropagation(); handleInternalPlaySample(voice.sampleUrl, voice.id); }}
                                                    disabled={!voice.sampleUrl}
                                                    aria-label={`Play sample for ${voice.name}`}
                                                    className="text-indigo-600 hover:text-indigo-700"
                                                >
                                                    <PlayCircle size={18} className="mr-1.5" /> Play Sample
                                                </Button>
                                                {activelyPlayingVoiceId === voice.id && <WaveformAnimation isPlaying={true}/>}
                                            </CardFooter>
                                        </motion.div>
                                    )
                                })}
                            </div>
                        )}
                    />
                    {(errors.ai_voice_id || errors.ai_voice_provider || errors.ai_name) && <p className="text-red-600 text-sm mt-2">Please select an AI Voice.</p>}

                    <AnimatePresence>
                    {selectedVoiceDetails && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-center"
                        >
                            <p className="text-green-700 font-medium flex items-center justify-center">
                                <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                                {selectedVoiceDetails.name} is ready to represent {businessNameToUse}!
                            </p>
                            <p className="text-xs text-green-600 mt-1">
                                Personality: {selectedVoiceDetails.traits?.join(' • ')}
                            </p>
                            {/* {isSetupComplete && !isSettingsDirty && (
                                <p className="mt-3 text-indigo-600 font-medium">
                                    Now let's set up {selectedVoiceDetails.name}'s greeting →
                                </p>
                            )} */}
                        </motion.div>
                    )}
                    </AnimatePresence>
                </motion.section>

                {/* Greeting Composer */}
                <motion.section
                     initial={{ opacity: 0, y: 20 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ delay: 0.2 }}
                     className="p-6 bg-white rounded-xl shadow-xl border border-indigo-100"
                >
                    <div className="flex items-center mb-2">
                        <Label htmlFor="ai_greeting_message" className="text-xl font-semibold block text-indigo-700">How Your AI Answers Calls</Label>
                        <InfoModal title="AI Greeting Message" description="This is the very first thing your AI employee will say when it answers an incoming phone call. Make it welcoming and clearly state the business name. Example: 'Hello, thank you for calling [Your Business Name]. How can I help you today?'" triggerClassName="ml-2"/>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">First impression matters! What should callers hear?</p>
                    <Controller
                        name="ai_greeting_message"
                        control={control}
                        render={({ field }) => (
                            <Textarea
                                id="ai_greeting_message"
                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 p-3 text-base"
                                rows={4}
                                placeholder={`Thanks for calling ${businessNameToUse}, this is ${aiNameToUse}. How can I help you today?`}
                                {...field}
                            />
                        )}
                    />
                    <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                        <span>Best greetings are 5-10 seconds (approx. {maxGreetingChars} characters)</span>
                        <span className={cn(greetingCharCount > maxGreetingChars ? "text-red-500 font-medium" : "text-gray-500")}>
                            {greetingCharCount}/{maxGreetingChars}
                        </span>
                    </div>
                    {errors.ai_greeting_message && <p className="text-red-600 text-sm mt-1">{errors.ai_greeting_message.message}</p>}

                    {selectedVoiceDetails && watchedGreeting && (
                         <Button
                            type="button"
                            variant="outline"
                            className="mt-4 text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                             disabled={true}
                         >
                            <Volume2 className="mr-2 h-5 w-5" /> Hear how this sounds with {selectedVoiceDetails.name}'s voice
                            <span className="ml-2 text-xs text-gray-400">(Preview Coming Soon)</span>
                         </Button>
                    )}

                    <div className="mt-8 p-6 bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200">
                        <div className="flex items-center mb-6">
                            <h3 className="text-lg font-semibold text-slate-800 flex items-center">
                                <Phone className="mr-2 h-5 w-5 text-indigo-600" />
                                Call Behavior Settings
                            </h3>
                            <InfoModal 
                                title="Call Behavior Settings" 
                                description={
                                    <div className="space-y-4">
                                        <p className="text-sm text-gray-700 mb-4">
                                            Control how your AI responds when calls begin. Most businesses should use "Greet immediately" for professional interactions.
                                        </p>

                                        {callBehaviorSettingsVideo && <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                            <iframe
                                                src={callBehaviorSettingsVideo || "https://www.loom.com/embed/b9488682b8304cb7ae56b52017b782f3?sid=7f628523-92d3-45b5-ae7a-9b1400386e95"}
                                                frameBorder="0"
                                                allowFullScreen
                                                className="absolute top-0 left-0 w-full h-full rounded-lg"
                                            />
                                        </div>}
                                        {callBehaviorSettingsVideo && <VideoLink to='call-behavior-settings' />}

                                        <div className="space-y-3">
                                            <div>
                                                <h4 className="font-medium mb-1">Inbound Calls (When Customers Call You):</h4>
                                                <ul className="text-sm space-y-1 text-gray-600">
                                                    <li><strong>Greet immediately:</strong> AI speaks first (recommended for most businesses)</li>
                                                    <li><strong>Wait for caller:</strong> AI waits for customer to speak first</li>
                                                </ul>
                                            </div>
                                            
                                            <div>
                                                <h4 className="font-medium mb-1">Outbound Calls (When AI Calls Out):</h4>
                                                <ul className="text-sm space-y-1 text-gray-600">
                                                    <li><strong>Introduce immediately:</strong> AI starts with clear introduction</li>
                                                    <li><strong>Wait for hello:</strong> AI waits for person to speak first</li>
                                                </ul>
                                            </div>
                                            
                                            <div>
                                                <h4 className="font-medium mb-1">Special Use Cases:</h4>
                                                <ul className="text-sm space-y-1 text-gray-600">
                                                    <li>Use "Wait for caller" if you have answering machine forwarding</li>
                                                    <li>Use "Wait for hello" for specialized outbound scenarios</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                }
                                triggerClassName="ml-2"
                            />
                        </div>
                        
                        <div className="space-y-6">
                            {/* Inbound Calls */}
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.1 }}
                                className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-white rounded-lg border border-slate-200 hover:border-indigo-200 transition-colors"
                            >
                                <div className="flex-1 mb-3 sm:mb-0 sm:mr-4">
                                    <div className="flex items-center mb-1">
                                        <div className="flex items-center">
                                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                                                <PhoneIncoming className="h-5 w-5 text-green-600" />
                                            </div>
                                            <div>
                                                <Label htmlFor="inbound_first_message_mode" className="text-base font-semibold text-slate-800">
                                                    When Customers Call You
                                                </Label>
                                                <p className="text-xs text-slate-500 mt-0.5">How should {aiNameToUse} answer incoming calls?</p>
                                            </div>
                                        </div>
                                        <InfoModal 
                                            title="Incoming Call Behavior" 
                                            description="Most businesses want their AI to greet callers right away with a professional welcome. Only use 'wait for caller' for specialized situations where silence is expected." 
                                            triggerClassName="ml-2"
                                        />
                                    </div>
                                </div>
                                <Controller 
                                    name="inbound_first_message_mode" 
                                    control={control} 
                                    render={({ field }) => (
                                        <Select value={field.value} onValueChange={field.onChange}>
                                            <SelectTrigger id="inbound_first_message_mode" className="w-[220px] border-slate-200 hover:border-indigo-300 transition-colors">
                                                <SelectValue placeholder="Choose behavior">
                                                    {field.value === 'assistant-speaks-first' && 'Greet immediately'}
                                                    {field.value === 'assistant-waits-for-user' && 'Wait for caller'}
                                                </SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="assistant-speaks-first" className="py-3">
                                                    <div className="flex items-center">
                                                        <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                                                        <span className="font-medium">Greet immediately</span>
                                                    </div>
                                                    <p className="text-xs text-slate-500 mt-0.5 ml-6">Professional & welcoming</p>
                                                </SelectItem>
                                                <SelectItem value="assistant-waits-for-user" className="py-3">
                                                    <div className="flex items-center">
                                                        <Clock className="h-4 w-4 mr-2 text-amber-500" />
                                                        <span className="font-medium">Wait for caller</span>
                                                    </div>
                                                    <p className="text-xs text-slate-500 mt-0.5 ml-6">Silent until they speak</p>
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </motion.div>

                            {/* Outbound Calls */}
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.2 }}
                                className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-white rounded-lg border border-slate-200 hover:border-indigo-200 transition-colors"
                            >
                                <div className="flex-1 mb-3 sm:mb-0 sm:mr-4">
                                    <div className="flex items-center mb-1">
                                        <div className="flex items-center">
                                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                                                <PhoneOutgoing className="h-5 w-5 text-blue-600" />
                                            </div>
                                            <div>
                                                <Label htmlFor="outbound_first_message_mode" className="text-base font-semibold text-slate-800">
                                                    When {aiNameToUse} Calls Out
                                                </Label>
                                                <p className="text-xs text-slate-500 mt-0.5">Following up on no-shows & reaching leads</p>
                                            </div>
                                        </div>
                                        <InfoModal 
                                            title="Outbound Call Behavior" 
                                            description="Starting with a clear introduction builds trust and reduces hang-ups. Your AI will identify your business and purpose right away. Only wait for hello in special circumstances." 
                                            triggerClassName="ml-2"
                                        />
                                    </div>
                                </div>
                                <Controller 
                                    name="outbound_first_message_mode" 
                                    control={control} 
                                    render={({ field }) => (
                                        <Select value={field.value} onValueChange={field.onChange}>
                                            <SelectTrigger id="outbound_first_message_mode" className="w-[220px] border-slate-200 hover:border-indigo-300 transition-colors">
                                                <SelectValue placeholder="Choose behavior">
                                                    {field.value === 'assistant-speaks-first-with-model-generated-message' && 'Introduce immediately'}
                                                    {field.value === 'assistant-waits-for-user' && 'Wait for hello'}
                                                </SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>                                        
                                                <SelectItem value="assistant-speaks-first-with-model-generated-message" className="py-3">
                                                    <div className="flex items-center">
                                                        <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                                                        <span className="font-medium">Introduce immediately</span>
                                                    </div>
                                                    <p className="text-xs text-slate-500 mt-0.5 ml-6">Builds trust & clarity</p>
                                                </SelectItem>
                                                <SelectItem value="assistant-waits-for-user" className="py-3">
                                                    <div className="flex items-center">
                                                        <Clock className="h-4 w-4 mr-2 text-amber-500" />
                                                        <span className="font-medium">Wait for hello</span>
                                                    </div>
                                                    <p className="text-xs text-slate-500 mt-0.5 ml-6">Let them speak first</p>
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </motion.div>
                        </div>

                        {/* Helpful tip */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className="mt-6 p-3 bg-indigo-50 rounded-lg border border-indigo-100"
                        >
                            <p className="text-sm text-indigo-700 flex items-start">
                                <Lightbulb className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                                <span><strong>Pro tip:</strong> 90% of businesses get best results with immediate greetings. It reduces confusion and makes your business appear more professional.</span>
                            </p>
                        </motion.div>
                    </div>                
                </motion.section>

                {/* Test is now accessible via the floating dock and header CTA */}

                {/* Quick Settings */}
                 <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                 >
                    <Card className="bg-slate-50 border-slate-200 shadow-md">
                        <CardHeader
                            className="flex flex-row items-center justify-between cursor-pointer py-4"
                            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                        >
                            <div className="flex items-center">
                                <Settings className="mr-3 h-6 w-6 text-slate-600" />
                                <CardTitle className="text-lg text-slate-700">Additional Settings</CardTitle>
                            </div>
                            {showAdvancedSettings ? <ChevronUp className="h-5 w-5 text-slate-500" /> : <ChevronDown className="h-5 w-5 text-slate-500" />}
                        </CardHeader>
                        <AnimatePresence>
                        {showAdvancedSettings && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <CardContent className="space-y-8 pt-6 pb-8 border-t border-slate-200">
                                    {/* Live Call Forwarding */}
                                    <div>
                                        <div className="flex items-center mb-1">
                                            <Label htmlFor="ai_forwarding_number" className="text-md font-semibold block">Live Call Forwarding</Label>
                                            <InfoModal
                                                title="Live Call Forwarding: Instant Human Support"
                                                description={
                                                    <div className="space-y-4">
                                                        <p className="text-sm text-gray-700 mb-4">
                                                           Ensure you never miss a critical opportunity. Your AI can seamlessly forward live calls to you or a team member when a caller needs human assistance.
                                                        </p>
                                                        <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-md">
                                                            <h4 className="font-medium text-indigo-800 mb-2">Here's how it works:</h4>
                                                            <ol className="list-decimal list-inside text-sm space-y-2 text-indigo-700">
                                                                <li>The AI places the caller on a brief hold.</li>
                                                                <li>It dials the number you provide below.</li>
                                                                <li><strong>It gives you a quick summary of the call</strong> (e.g., "This is a new lead asking about pricing.").</li>
                                                                <li>You accept, and the call is instantly connected.</li>
                                                            </ol>
                                                        </div>
                                                        <p className="text-sm text-gray-700">The caller ID will show the customer's number, so you always know who's calling.</p>
                                                    </div>
                                                }
                                                triggerClassName="ml-1"
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 mb-2">Your AI can forward live calls to you when needed. We'll provide a quick summary of the call before connecting.</p>
                                        <Controller
                                            name="ai_forwarding_number"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    id="ai_forwarding_number"
                                                    value={field.value ? formatPhoneNumber(field.value.replace(/^\+1/, '')) : ''}
                                                    onChange={(e) => {
                                                        let rawValue = e.target.value.replace(/[^\d]/g, '');
                                                        if (rawValue.length > 10) rawValue = rawValue.substring(0, 10);
                                                        field.onChange(rawValue ? `+1${rawValue}`: '');
                                                    }}
                                                    placeholder="e.g., (555) 123-4567 (Your cell or office line)"
                                                    className="mt-1 block w-full"
                                                    type="tel"
                                                />
                                            )}
                                        />
                                        {errors.ai_forwarding_number && <p className="text-red-600 text-sm mt-1">{errors.ai_forwarding_number.message}</p>}
                                    </div>

                                    {/* Voicemail Instructions */}
                                    <div>
                                        <div className="flex items-center mb-1">
                                            <Label htmlFor="voicemail_instructions_text" className="text-md font-semibold block">Outbound Voicemail Message</Label>
                                            <InfoModal
                                                title="Outbound Voicemail Message"
                                                description={
                                                    <div className="space-y-4">
                                                        <p className="text-sm text-gray-700 mb-4">
                                                            Customize what message your AI leaves when it reaches a voicemail during outbound calls (appointment confirmations, reschedules, etc.). This replaces the default voicemail message.
                                                        </p>
                                                        <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                                                            <h4 className="font-medium text-green-800 mb-2">Voicemail Best Practices:</h4>
                                                            <ul className="list-disc list-inside text-sm space-y-1 text-green-700">
                                                                <li>Identify yourself and your business clearly</li>
                                                                <li>State the general purpose of the call</li>
                                                                <li>Leave a clear callback number</li>
                                                                <li>Keep it brief and professional</li>
                                                            </ul>
                                                        </div>
                                                        <p className="text-sm text-gray-700">Leave this blank to use the default voicemail message format.</p>
                                                    </div>
                                                }
                                                triggerClassName="ml-1"
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 mb-2">What message should your AI leave when reaching voicemail on outbound calls?</p>
                                        <Controller
                                            name="voicemail_instructions_text"
                                            control={control}
                                            render={({ field }) => (
                                                <Textarea
                                                    id="voicemail_instructions_text"
                                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 p-3 text-base bg-white"
                                                    rows={4}
                                                    placeholder="Example: Hi [customer name], this is [AI name] from [business]. I'm calling about your appointment. Please call us back at [phone number] at your earliest convenience. Thank you!"
                                                    {...field}
                                                    value={field.value ?? ''}
                                                />
                                            )}
                                        />
                                        {errors.voicemail_instructions_text && <p className="text-red-600 text-sm mt-1">{errors.voicemail_instructions_text.message}</p>}
                                    </div>

                                    <div>
                                        <div className="flex items-center mb-1">
                                            <Label htmlFor="reply_to_email" className="text-md font-semibold block">Reply-To email (your inbox)</Label>
                                            <InfoModal 
                                                 title="Reply-To email (your inbox)" 
                                                description={
                                                    <div className="space-y-4">
                                                        <p className="text-sm text-gray-700 mb-4">
                                                            This email address receives replies to emails sent by your AI. When customers respond to AI-sent emails, you'll receive those replies at this address.
                                                        </p>
                                                        
                                                        {replyToEmailVideo && <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                                            <iframe
                                                                src={replyToEmailVideo || "https://www.loom.com/embed/7a6153c867da41d9b608deeab34c4bea?sid=542788cf-0beb-4c23-89f8-e2f77360454c"}
                                                                frameBorder="0"
                                                                allowFullScreen
                                                                className="absolute top-0 left-0 w-full h-full rounded-lg"
                                                            />
                                                        </div>}
                                                        {replyToEmailVideo && <VideoLink to='reply-to-email' />}

                                                        <div className="space-y-3">
                                                            <div>
                                                                 <h4 className="font-medium mb-1">How it works</h4>
                                                                <ul className="text-sm space-y-1 text-gray-600">
                                                                    <li>AI sends emails using our domain for better delivery</li>
                                                                    <li>Customer replies go to your specified email address</li>
                                                                    <li>You receive all customer responses in your inbox</li>
                                                                </ul>
                                                            </div>
                                                            
                                                            <div>
                                                                 <h4 className="font-medium mb-1">Common AI emails</h4>
                                                                <ul className="text-sm space-y-1 text-gray-600">
                                                                    <li>Appointment confirmations</li>
                                                                    <li>Conversation recaps</li>
                                                                    <li>Follow-up information</li>
                                                                    <li>Booking details</li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                }
                                                triggerClassName="ml-1"
                                            />
                                        </div>
                                         <p className="text-xs text-gray-500 mb-2">Replies to AI emails go to this address.</p>
                                        <Input
                                            id="reply_to_email"
                                            {...register('reply_to_email')}
                                            placeholder={"you@example.com"} // Removed auto-fill
                                            className="mt-1 block w-full"
                                            type="email"
                                        />
                                        {errors.reply_to_email && <p className="text-red-600 text-sm mt-1">{errors.reply_to_email.message}</p>}
                                    </div>
                                    <div className="pt-6 border-t border-slate-300">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <Label htmlFor="hipaa_enabled" className="text-md font-semibold">Medical Privacy Mode (HIPAA)</Label>
                                                <InfoModal 
                                                    title="Medical Privacy Mode (HIPAA)" 
                                                    description={
                                                        <div className="space-y-4">
                                                            <p className="text-sm text-gray-700 mb-4">
                                                                For healthcare providers: Enable this if you handle Protected Health Information (PHI) and need to comply with HIPAA regulations.
                                                            </p>
                                                            
                                                            {medicalPrivacyModeVideo && <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                                                <iframe
                                                                    src={medicalPrivacyModeVideo || "https://www.loom.com/embed/f0438b959fdf44f49a47b0b84d66bb60?sid=3ee63053-a61b-4857-981c-9ed9ffd866b3"}
                                                                    frameBorder="0"
                                                                    allowFullScreen
                                                                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                                                                />
                                                            </div>}
                                                            {medicalPrivacyModeVideo && <VideoLink to='medical-privacy-mode' />}

                                                            <div className="space-y-3">
                                                                <div>
                                                                    <h4 className="font-medium mb-1">What Gets Disabled:</h4>
                                                                    <ul className="text-sm space-y-1 text-gray-600">
                                                                        <li>Call recordings are turned off</li>
                                                                        <li>Call transcripts are unavailable</li>
                                                                        <li>Call outcomes cannot be determined</li>
                                                                        <li>Call summaries are disabled</li>
                                                                    </ul>
                                                                </div>
                                                                
                                                                <div>
                                                                    <h4 className="font-medium mb-1">What Still Works:</h4>
                                                                    <ul className="text-sm space-y-1 text-gray-600">
                                                                        <li>AI can still answer and handle calls</li>
                                                                        <li>Appointment booking functionality</li>
                                                                        <li>Basic call handling and routing</li>
                                                                        <li>Email confirmations (if enabled)</li>
                                                                    </ul>
                                                                </div>
                                                                
                                                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                                                                    <p className="text-sm text-amber-700">
                                                                        <strong>Note:</strong> We're working on an improved version that will allow you to retain recordings, transcripts, and outcomes while maintaining HIPAA compliance.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    }
                                                    triggerClassName="ml-1"
                                                />
                                            </div>
                                            <Controller name="hipaa_enabled" control={control} render={({ field }) => ( <Switch id="hipaa_enabled" checked={field.value ?? false} onCheckedChange={field.onChange} aria-label="Enable HIPAA Compliance Mode" /> )}/>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">Keeps patient information private • No recordings saved • Disables transcripts & outcomes.</p>
                                        {errors.hipaa_enabled && <p className="text-red-600 text-sm mt-1">{errors.hipaa_enabled.message}</p>}
                                    </div>
                                    <div className="pt-6 border-t border-slate-300">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <Label htmlFor="background_noise_enabled" className="text-md font-semibold">Office Phone Ambiance</Label>
                                                <InfoModal title="Office Phone Ambiance" description="Add subtle background sounds during calls." triggerClassName="ml-1"/>
                                            </div>
                                            <Controller name="background_noise_enabled" control={control} render={({ field }) => ( <Switch id="background_noise_enabled" checked={field.value ?? true} onCheckedChange={field.onChange} aria-label="Enable office phone ambiance" /> )}/>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">Includes ambient office sounds during calls. Turn off for silence.</p>
                                    </div>
                                    </CardContent>
                            </motion.div>
                        )}
                        </AnimatePresence>
                    </Card>
                </motion.section>

                <div className="pt-8 border-t mt-8 flex items-center gap-2">
                    <Button type="submit" disabled={!isSettingsDirty || isSaving} className="w-full sm:w-auto text-base px-6 py-3">
                        {isSaving ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Saving AI Employee...</>) :
                        (isSetupComplete ? (<><CheckCircle className="mr-2 h-5 w-5" /> Save AI Employee</>) :
                        (<><Sparkles className="mr-2 h-5 w-5" /> Save AI Employee</>))}
                    </Button>
                    {!isSettingsDirty && !isSaving && !isSettingsFetching && (<p className="text-sm text-gray-500 mt-2 inline-block ml-4">No changes to save.</p>)}
                    {isSettingsFetching && <Loader2 className="ml-4 h-5 w-5 animate-spin inline-block text-gray-400" />}
                    {isSettingsDirty && !isSaving && (<p className="text-sm text-green-500 mt-2 inline-block ml-4">You have unsaved changes for your AI Employee. Don't forget to save! </p>)}
                </div>
            </CardContent>
        </Card>
    );
}